import React from 'react'
import { components } from 'react-select'
import { observer } from 'mobx-react'
import { useDebounce } from 'hooks/useDebounce'
import { useStores } from 'stores'
import FormGroup from 'common/FormGroup'
import RadioGroup from 'common/Forms/RadioGroup'
import ReactSelect from 'common/Forms/ReactSelect'
import WebformInfoDeliveryInfoAddress from 'components/Webform/WebformInfoDeliveryInfoAddress'
import Checkbox from 'common/Forms/Checkbox'
import PlaceholderOption from 'common/PlaceholderOption'
import ShowMoreButton from 'common/ShowMoreButton'

const CLIENT = 'client'
const OTHER = 'other'

export default observer(({ resource, data, addressType: addressTypeProp, namePrefix = '', lineIndex, disabled }) => {
  const [addressType, setAddressType] = React.useState(addressTypeProp || CLIENT)
  const [inputValue, setInputValue] = React.useState('')
  const [isChecked, setIsChecked] = React.useState(true)
  const [options, setOptions] = React.useState([])
  const [page, setPage] = React.useState(1)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [menuIsOpen, setMenuIsOpen] = React.useState(undefined)
  const [hasSelected, setHasSelected] = React.useState(false)
  const pageSize = 50

  const { deliveryInfo } = data
  const { clientAreaStore } = useStores()
  const isClient = addressType === CLIENT
  const isOther = addressType === OTHER
  const { settings = {} } = data

  const handleChangeAddressType = selected => {
    setAddressType(selected)
  }

  const loadOptions = React.useCallback(
    async (search, page) => {
      setLoadingMore(true)
      const response = await clientAreaStore.getClientSites({ search, pageSize, page })
      setLoadingMore(false)

      if (page === 1) {
        setOptions(response.data)
      } else {
        setOptions(prev => [...prev, ...response.data])
      }

      const input = document.querySelector(`[id="${namePrefix}deliveryInfo.clientAddress"] input`)
      if (input) {
        input.focus()
      }
    },
    [clientAreaStore, pageSize, namePrefix]
  )

  React.useEffect(() => {
    loadOptions(inputValue, page)
  }, [inputValue, page, loadOptions])

  const debouncedSetInputValue = useDebounce(value => {
    setInputValue(value)
    setPage(1)
    setHasSelected(false)
  }, 300)

  const handleInputChange = (newValue, actionMeta) => {
    // Only handle user input, not other events
    if (actionMeta.action === 'input-change') {
      debouncedSetInputValue(newValue)
      return newValue
    }
    return newValue
  }

  const handleChange = () => {
    // Clear the input value after selecting an option
    setInputValue('')
    setPage(1)
    setHasSelected(true)
  }

  const handleMenuOpen = () => {
    setMenuIsOpen(true)
  }

  const handleMenuClose = () => {
    setMenuIsOpen(false)
  }

  const handleBlur = () => {
    // When input loses focus and no selection was made, load default list
    const hasSearchTerm = inputValue && inputValue.trim().length > 0

    if (hasSearchTerm && !hasSelected) {
      // Load default list without search term
      setInputValue('')
      setPage(1)
      setHasSelected(false)
    }
  }

  const optionsWithLoadMore = React.useMemo(() => {
    const result = Array.isArray(options) ? [...options] : []
    const totalLoaded = result.length
    const hasMore = totalLoaded > 0 && totalLoaded === pageSize * page

    if (hasMore) {
      result.push({ isLoadMore: true, label: resource.ShowMore })
    }

    return result
  }, [options, page, pageSize, resource])

  return (
    <React.Fragment>
      <FormGroup
        formGroup
        horizontal
        label={resource.DeliveryAddress}
        inputColClassName="d-flex flex-sm-row flex-column"
      >
        <div className="col-sm-5 col-12">
          <RadioGroup
            disabled={disabled}
            radios={[
              {
                value: CLIENT,
                label: resource.CompanySite,
              },
              {
                value: OTHER,
                label: resource.OtherAddress,
              },
            ]}
            name={`${namePrefix}deliveryInfo.addressType`}
            label={resource.DeliveryAddress}
            defaultValue={addressType}
            onChange={handleChangeAddressType}
          />
        </div>
        <div className="col-sm-7 col-12">
          {isClient && (
            <ReactSelect
              id={`${namePrefix}deliveryInfo.clientAddress`}
              isDisabled={disabled}
              isSearchable={true}
              name={`${namePrefix}deliveryInfo.clientAddress`}
              rules={{ required: isClient && resource.Required }}
              defaultValue={deliveryInfo.clientSite}
              label={resource.CompanySite}
              options={optionsWithLoadMore}
              onInputChange={handleInputChange}
              onChange={handleChange}
              onMenuOpen={handleMenuOpen}
              onMenuClose={handleMenuClose}
              onBlur={handleBlur}
              menuIsOpen={menuIsOpen}
              isLoading={loadingMore}
              filterOption={null}
              optionValueKey="clientSiteId"
              optionLabelKey="name"
              components={{
                Option: ({ data, innerRef, ...innerProps }) => {
                  if (data.isPlaceholder) {
                    return <PlaceholderOption ref={innerRef} label={data.label} />
                  }

                  if (data.isLoadMore) {
                    return (
                      <ShowMoreButton
                        resource={resource}
                        disabled={loadingMore}
                        innerProps={innerProps}
                        setPage={setPage}
                      />
                    )
                  }
                  const address = data.address || {}
                  return (
                    <components.Option data={data} {...innerProps}>
                      <strong className="d-block">{data.name}</strong>
                      <small className="d-block muted address-mini">{`${address.street || ''} ${address.number || ''} ${
                        address.box || ''
                      }, ${address.postalCode || ''} ${address.city || ''}, ${address.country ? address.country.name : ''}`}</small>
                    </components.Option>
                  )
                },
              }}
            />
          )}
        </div>
      </FormGroup>
      {isOther && (
        <WebformInfoDeliveryInfoAddress
          {...{ resource, deliveryAddress: deliveryInfo.deliveryAddress, isOther, namePrefix, lineIndex, disabled }}
        />
      )}
      {settings.enableWebformService && (
        <FormGroup formGroup horizontal label={resource.Service} inputColClassName="d-flex flex-sm-row flex-column">
          <Checkbox
            name={`${namePrefix}deliveryInfo.isService`}
            horizontal
            checked={isChecked}
            onChange={e => setIsChecked(e.target.checked)}
          />
        </FormGroup>
      )}
    </React.Fragment>
  )
})